import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Menu, X, Plane, Clock, Shield, Globe2, Star, ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/demo/lib/Reveal";
import jetPhenom from "@/assets/skyelite-jet-phenom.jpg";
import jetCitation from "@/assets/skyelite-jet-citation.jpg";
import jetChallenger from "@/assets/skyelite-jet-challenger.jpg";
import jetGlobal from "@/assets/skyelite-jet-global.jpg";
import cabinLight from "@/assets/skyelite-cabin-light.jpg";
import cabinMid from "@/assets/skyelite-cabin-mid.jpg";
import cabinHeavy from "@/assets/skyelite-cabin-heavy.jpg";
import cabinGlobal from "@/assets/skyelite-cabin-global.jpg";
import routeStTropez from "@/assets/skyelite-route-sttropez.jpg";
import routeMykonos from "@/assets/skyelite-route-mykonos.jpg";
import routeDubai from "@/assets/skyelite-route-dubai.jpg";
import routeParis from "@/assets/skyelite-route-paris.jpg";
import routeIbiza from "@/assets/skyelite-route-ibiza.jpg";
import routeLondon from "@/assets/skyelite-route-london.jpg";

const WA = "41782369200";
const VIDEO = "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260328_091828_e240eb17-6edc-4129-ad9d-98678e3fd238.mp4";
const ACCENT = "#202A36";

const FLEET = [
  { id: "1", name: "Phenom 300E", cat: "Light Jet", pax: 7, range: 3650, speed: 859, hourly: 4400, img: cabinLight, ex: jetPhenom },
  { id: "2", name: "Citation Latitude", cat: "Midsize Jet", pax: 9, range: 5278, speed: 826, hourly: 6800, img: cabinMid, ex: jetCitation },
  { id: "3", name: "Challenger 605", cat: "Heavy Jet", pax: 12, range: 7400, speed: 870, hourly: 9200, img: cabinHeavy, ex: jetChallenger },
  { id: "4", name: "Global 7500", cat: "Ultra Long Range", pax: 17, range: 14260, speed: 982, hourly: 15800, img: cabinGlobal, ex: jetGlobal },
];

const ROUTES = [
  { from: "Lugano", to: "Saint-Tropez", duration: "1h 20m", price: 12400, img: routeStTropez },
  { from: "Milano", to: "Mykonos", duration: "2h 35m", price: 18200, img: routeMykonos },
  { from: "Zurigo", to: "Dubai", duration: "6h 05m", price: 64500, img: routeDubai },
  { from: "Lugano", to: "Paris Le Bourget", duration: "1h 35m", price: 14600, img: routeParis },
  { from: "Milano", to: "Ibiza", duration: "1h 55m", price: 13800, img: routeIbiza },
  { from: "Zurigo", to: "London Farnborough", duration: "1h 50m", price: 16200, img: routeLondon },
];

const BENEFITS = [
  { icon: Clock, t: "Decollo in 4 ore", d: "Concierge attivo 24/7. Slot garantito da Lugano-Agno, Milano Linate e Zurigo." },
  { icon: Globe2, t: "190+ aeroporti", d: "Copertura globale con flotta certificata ETOPS per voli intercontinentali." },
  { icon: Shield, t: "EASA Part-NCC", d: "Audit IS-BAO Stage III e Wyvern Wingman. Comandanti con 5.000+ ore." },
  { icon: Plane, t: "Flotta moderna", d: "Età media 4.2 anni. Manutenzione AOG presso hangar certificati Bombardier e Embraer." },
];

const EUR_RATE = 1 / 0.92; // 1 CHF = 1/0.92 EUR... we convert CHF -> EUR by dividing by 0.92? Spec: 1 EUR = 0.92 CHF, so EUR = CHF / 0.92
function fmt(n: number, cur: "CHF" | "EUR") {
  const value = cur === "CHF" ? n : n / 0.92;
  const rounded = Math.round(value);
  return cur + " " + rounded.toLocaleString("it-CH");
}

function SkyElite() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currency, setCurrency] = useState<"CHF" | "EUR">("CHF");
  const chf = (n: number) => fmt(n, currency);
  const [picked, setPicked] = useState(FLEET[1]);
  const [hours, setHours] = useState(3);
  const [from, setFrom] = useState("Lugano (LUG)");
  const [to, setTo] = useState("Saint-Tropez (LTT)");

  const total = useMemo(() => picked.hourly * hours + 1800, [picked, hours]);

  const nav = [
    { l: "Start", h: "#start" },
    { l: "Fleet", h: "#fleet" },
    { l: "Routes", h: "#routes" },
    { l: "Benefits", h: "#benefits" },
    { l: "FAQ", h: "#faq" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 antialiased overflow-x-hidden" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .se-num { font-feature-settings: 'tnum' 1; }
      `}</style>

      {/* HERO with VIDEO */}
      <section id="start" className="relative h-screen overflow-hidden bg-gray-900">
        <video
          src={VIDEO}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-white/70" />

        {/* NAV */}
        <header className="relative z-20">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between">
            <div className="text-2xl font-semibold text-gray-900 tracking-tight">SkyElite</div>
            <nav className="hidden md:flex gap-8 text-sm font-medium text-gray-900">
              {nav.map((n) => (
                <a key={n.l} href={n.h} className="hover:text-gray-600 transition-colors">{n.l}</a>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-1 mr-2 rounded-full border border-gray-300 bg-white/70 p-0.5 text-xs font-medium">
              {(["CHF", "EUR"] as const).map((c) => (
                <button key={c} onClick={() => setCurrency(c)} className={`px-3 py-1 rounded-full transition ${currency === c ? "text-white" : "text-gray-700 hover:text-gray-900"}`} style={currency === c ? { background: ACCENT } : undefined}>{c}</button>
              ))}
            </div>
            <a href="#quote" className="hidden md:inline-flex px-4 py-2 rounded-full text-white text-sm font-medium transition-colors" style={{ background: ACCENT }}>Book Now</a>
            <div className="md:hidden flex items-center gap-1 mr-1 rounded-full border border-gray-300 bg-white/80 p-0.5 text-[11px] font-medium">
              {(["CHF", "EUR"] as const).map((c) => (
                <button key={c} onClick={() => setCurrency(c)} className={`px-2 py-1 rounded-full transition ${currency === c ? "text-white" : "text-gray-700"}`} style={currency === c ? { background: ACCENT } : undefined}>{c}</button>
              ))}
            </div>
            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-gray-900" aria-label="Menu">
              {menuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
          {menuOpen && (
            <div className="md:hidden absolute top-full left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl p-4 flex flex-col gap-1">
              {nav.map((n) => (
                <a key={n.l} href={n.h} onClick={() => setMenuOpen(false)} className="px-4 py-3 rounded-lg hover:bg-gray-100 text-gray-900 font-medium">{n.l}</a>
              ))}
              <a href="#quote" onClick={() => setMenuOpen(false)} className="mt-2 px-4 py-3 rounded-full text-white text-center font-medium" style={{ background: ACCENT }}>Book Now</a>
            </div>
          )}
        </header>

        {/* HERO CONTENT */}
        <div className="relative z-10 h-[calc(100vh-88px)] flex flex-col items-center justify-center text-center px-6 -mt-10 sm:-mt-20">
          <div className="text-xs sm:text-sm font-semibold text-gray-700 tracking-[0.25em] uppercase mb-4">— Private Jets</div>
          <h1 className="leading-none tracking-tighter font-normal">
            <span className="block text-6xl md:text-7xl lg:text-8xl text-gray-500">Premium.</span>
            <span className="block text-6xl md:text-7xl lg:text-8xl -mt-3" style={{ color: ACCENT }}>Accessible.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-gray-700 max-w-2xl">Your dedication deserves recognition. Voli charter su misura da Lugano, Milano e Zurigo verso 190+ destinazioni nel mondo.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <a href="#fleet" className="px-5 py-2.5 rounded-full bg-gray-200/90 backdrop-blur text-gray-900 font-medium hover:bg-gray-300 transition-colors">Discover</a>
            <a href="#quote" className="px-5 py-2.5 rounded-full text-white font-medium transition-colors" style={{ background: ACCENT }}>Book Now</a>
          </div>
          <div className="mt-12 flex flex-wrap gap-x-10 gap-y-3 justify-center text-xs sm:text-sm text-gray-700">
            <div><span className="font-semibold text-gray-900">4h</span> wheels up</div>
            <div><span className="font-semibold text-gray-900">190+</span> aeroporti</div>
            <div><span className="font-semibold text-gray-900">24/7</span> concierge</div>
            <div><span className="font-semibold text-gray-900">4.9★</span> 1.200+ voli</div>
          </div>
        </div>
      </section>

      {/* STORY / BENEFITS */}
      <section id="benefits" className="px-6 sm:px-10 py-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="max-w-2xl mb-16">
              <div className="text-xs font-semibold tracking-[0.25em] uppercase text-gray-500 mb-4">— Why SkyElite</div>
              <h2 className="text-4xl md:text-6xl font-normal tracking-tight" style={{ color: ACCENT }}>
                <span className="text-gray-400">Il tempo è</span><br/>il vero lusso.
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">Operatore charter svizzero dal 2009. Hub principali a Lugano-Agno, Milano Linate e Zurigo Kloten. Flotta di proprietà certificata EASA, equipaggio dedicato, fatturazione conforme VAT CH/EU.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="p-7 bg-gray-50 rounded-2xl h-full border border-gray-100 hover:border-gray-300 transition-colors">
                  <b.icon size={28} className="mb-5" style={{ color: ACCENT }} />
                  <div className="font-semibold text-lg mb-2">{b.t}</div>
                  <p className="text-sm text-gray-600 leading-relaxed">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section id="fleet" className="px-6 sm:px-10 py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <div>
                <div className="text-xs font-semibold tracking-[0.25em] uppercase text-gray-500 mb-4">— Fleet</div>
                <h2 className="text-4xl md:text-6xl font-normal tracking-tight" style={{ color: ACCENT }}>Quattro categorie.<br/><span className="text-gray-400">Una sola classe.</span></h2>
              </div>
              <p className="text-gray-600 max-w-sm text-sm">Tocca un aeromobile per simularne il preventivo. Tutti i jet sono di proprietà SkyElite e operati da equipaggio interno.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {FLEET.map((f, i) => {
              const active = picked.id === f.id;
              return (
                <Reveal key={f.id} delay={i * 60}>
                  <button onClick={() => setPicked(f)} className={`group block w-full text-left rounded-2xl overflow-hidden border-2 bg-white transition-all ${active ? "border-gray-900 shadow-xl" : "border-transparent hover:border-gray-200 shadow-sm hover:shadow-lg"}`}>
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img src={f.img} alt={`${f.name} interior`} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                      <div className="absolute top-4 left-4 text-[10px] font-semibold tracking-[0.2em] uppercase bg-white/90 backdrop-blur text-gray-900 px-3 py-1.5 rounded-full">{f.cat}</div>
                      {active && <div className="absolute top-4 right-4 bg-gray-900 text-white rounded-full p-1.5"><Check size={14} /></div>}
                    </div>
                    <div className="p-5 flex items-end justify-between gap-4">
                      <div>
                        <div className="text-xl font-semibold">{f.name}</div>
                        <div className="text-sm text-gray-500 mt-1 se-num">{f.pax} pax · {f.range.toLocaleString()} km · {f.speed} km/h</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold se-num">{chf(f.hourly)}</div>
                        <div className="text-[10px] uppercase tracking-widest text-gray-400">per ora</div>
                      </div>
                    </div>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROUTES */}
      <section id="routes" className="px-6 sm:px-10 py-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-gray-500 mb-4">— Popular routes</div>
            <h2 className="text-4xl md:text-6xl font-normal tracking-tight mb-14" style={{ color: ACCENT }}>Dalla Svizzera, <span className="text-gray-400">verso il mondo.</span></h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ROUTES.map((r, i) => (
              <Reveal key={i} delay={i * 50}>
                <a href="#quote" className="group block rounded-2xl overflow-hidden bg-gray-50 border border-gray-100 hover:shadow-xl transition-all">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={r.img} alt={`${r.from} to ${r.to}`} className="w-full h-full object-cover group-hover:scale-110 transition duration-1000" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute bottom-4 left-5 right-5 text-white flex items-center justify-between text-sm font-medium">
                      <span>{r.from}</span>
                      <ArrowRight size={16} />
                      <span>{r.to}</span>
                    </div>
                  </div>
                  <div className="p-5 flex items-end justify-between">
                    <div>
                      <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-gray-500">{r.duration} di volo</div>
                      <div className="text-2xl font-semibold mt-1 se-num">da {chf(r.price)}</div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 group-hover:underline">Richiedi →</span>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section id="quote" className="px-6 sm:px-10 py-24" style={{ background: ACCENT }}>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[2fr_3fr] gap-12 text-white">
          <div>
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-white/60 mb-4">— Rates</div>
            <h2 className="text-4xl md:text-6xl font-normal tracking-tight leading-tight">Configura<br/><span className="text-white/50">il tuo volo.</span></h2>
            <p className="mt-6 text-white/70 max-w-md">Stima trasparente basata sull'aeromobile selezionato. Handling, tasse e catering inclusi. Conferma slot entro 2 ore.</p>
            <div className="mt-8 p-5 rounded-2xl bg-white/5 border border-white/10">
              <div className="text-[10px] tracking-[0.25em] uppercase text-white/60 mb-2">Aeromobile selezionato</div>
              <div className="text-xl font-semibold">{picked.name}</div>
              <div className="text-sm text-white/60 mt-1">{picked.cat} · {picked.pax} pax · {picked.range.toLocaleString()} km</div>
            </div>
          </div>
          <div className="bg-white text-gray-900 rounded-3xl p-8">
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div>
                <div className="text-xs font-semibold tracking-wider uppercase text-gray-500 mb-2">Da</div>
                <input value={from} onChange={(e) => setFrom(e.target.value)} className="w-full bg-transparent border-b border-gray-200 py-2 text-lg focus:outline-none focus:border-gray-900 transition" />
              </div>
              <div>
                <div className="text-xs font-semibold tracking-wider uppercase text-gray-500 mb-2">A</div>
                <input value={to} onChange={(e) => setTo(e.target.value)} className="w-full bg-transparent border-b border-gray-200 py-2 text-lg focus:outline-none focus:border-gray-900 transition" />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold tracking-wider uppercase text-gray-500 mb-3 flex justify-between">
                <span>Ore di volo</span>
                <span className="se-num text-gray-900">{hours}h</span>
              </div>
              <input type="range" min={1} max={12} value={hours} onChange={(e) => setHours(+e.target.value)} className="w-full accent-gray-900" />
            </div>
            <div className="mt-10 pt-8 border-t border-gray-100 grid sm:grid-cols-3 gap-6">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500">Costo orario</div>
                <div className="text-xl font-semibold mt-2 se-num">{chf(picked.hourly)}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500">Handling & tasse</div>
                <div className="text-xl font-semibold mt-2">inclusi</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: ACCENT }}>Totale stimato</div>
                <div className="text-3xl font-semibold mt-2 se-num" style={{ color: ACCENT }}>{chf(total)}</div>
              </div>
            </div>
            <a href={`https://wa.me/${WA}?text=${encodeURIComponent(`Buongiorno SkyElite, vorrei un preventivo: ${from} → ${to}, ${picked.name}, ${hours}h. Grazie.`)}`} className="mt-8 block text-center text-white py-4 rounded-full font-medium hover:opacity-90 transition-opacity" style={{ background: ACCENT }}>Conferma su WhatsApp</a>
          </div>
        </div>
      </section>

      {/* PRESS */}
      <section className="px-6 sm:px-10 py-16 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center text-xs font-semibold tracking-[0.25em] uppercase text-gray-500 mb-8">— Featured in</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-gray-400 text-xl md:text-2xl font-serif italic">
            <span>Corriere del Ticino</span><span>·</span>
            <span>NZZ am Sonntag</span><span>·</span>
            <span>Robb Report</span><span>·</span>
            <span>Bilanz</span><span>·</span>
            <span>Monocle</span>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="px-6 sm:px-10 py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-gray-500 mb-4">— Voci dei clienti</div>
            <h2 className="text-4xl md:text-6xl font-normal tracking-tight mb-14" style={{ color: ACCENT }}>La parola <span className="text-gray-400">a chi vola.</span></h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { q: "Quattro voli in due settimane, sempre puntuali al minuto. Il concierge ha gestito anche transfer e meeting room a Saint-Tropez. Servizio chirurgico.", n: "Marco Rezzonico", r: "CEO, Rezzonico Holding · Lugano" },
              { q: "Da quando voliamo con SkyElite abbiamo dimezzato i tempi di trasferta del board. La Citation Latitude è perfetta per la rotta Lugano–Londra.", n: "Anna Berger", r: "Group CFO, Berger Pharma · Zurigo" },
              { q: "Discrezione, professionalità e attenzione ai dettagli. Il Global 7500 ha reso confortevole anche un Milano–Dubai non-stop con la famiglia.", n: "Family Office (riservato)", r: "Cliente dal 2018 · 51 voli" },
            ].map((t, i) => (
              <Reveal key={i} delay={i * 80}>
                <figure className="bg-white rounded-2xl p-7 h-full flex flex-col justify-between border border-gray-100">
                  <div>
                    <div className="flex gap-0.5 mb-4 text-amber-500">
                      {Array.from({ length: 5 }).map((_, k) => <Star key={k} size={14} fill="currentColor" />)}
                    </div>
                    <blockquote className="text-gray-800 leading-relaxed">"{t.q}"</blockquote>
                  </div>
                  <figcaption className="mt-6 pt-5 border-t border-gray-100">
                    <div className="font-semibold text-sm" style={{ color: ACCENT }}>{t.n}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{t.r}</div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 sm:px-10 py-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-gray-500 mb-4">— FAQ</div>
            <h2 className="text-4xl md:text-6xl font-normal tracking-tight mb-12" style={{ color: ACCENT }}>Tutto quello che <span className="text-gray-400">devi sapere.</span></h2>
          </Reveal>
          <div className="divide-y divide-gray-100 border-y border-gray-100">
            {[
              { q: "In quanto tempo posso decollare?", a: "Decollo garantito in 4 ore da Lugano-Agno per Light e Midsize, 6 ore per Heavy e Ultra Long Range. Concierge dedicato attivo 24/7." },
              { q: "Quali aeroporti coprite?", a: "Operiamo verso oltre 190 aeroporti in Europa, Medio Oriente, Nord Africa e Asia. Per voli intercontinentali utilizziamo flotta Heavy e Ultra Long Range certificata ETOPS-180." },
              { q: "Come funziona il pagamento?", a: "Bonifico bancario, carta di credito o jet card con sconti progressivi. Fatturazione conforme VAT svizzera ed europea, conto deposito ore di volo disponibile per clienti corporate." },
              { q: "Quali certificazioni avete?", a: "Operatore EASA Part-NCC con audit IS-BAO Stage III e Wyvern Wingman. Tutti i comandanti hanno oltre 5.000 ore di volo, doppio rating e formazione CRM annuale." },
              { q: "Posso portare animali a bordo?", a: "Sì. Animali domestici sono benvenuti senza supplemento, con servizio veterinario opzionale per rotte intercontinentali." },
              { q: "Compensate le emissioni di CO₂?", a: "Programma di carbon offset attivabile su ogni volo, con partner certificati Gold Standard. Reporting trimestrale incluso per clienti corporate." },
            ].map((f, i) => (
              <details key={i} className="group py-6">
                <summary className="flex justify-between items-center cursor-pointer list-none">
                  <span className="text-lg font-medium pr-6">{f.q}</span>
                  <span className="text-2xl text-gray-400 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="px-6 sm:px-10 py-32 text-center" style={{ background: ACCENT }}>
        <div className="max-w-3xl mx-auto text-white">
          <h2 className="text-5xl md:text-7xl font-normal tracking-tight">Decollo in <span className="text-white/50">4 ore.</span></h2>
          <p className="mt-6 text-white/70 max-w-lg mx-auto">Concierge attivo 24/7. Risposta entro 15 minuti. Discrezione assoluta.</p>
          <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Buongiorno SkyElite, vorrei informazioni per un volo charter.")}`} className="mt-10 inline-block bg-white text-gray-900 px-10 py-4 rounded-full font-medium hover:bg-gray-100 transition-colors">Contatta il concierge</a>
        </div>
      </section>

      <footer className="px-6 sm:px-10 py-10 bg-white border-t border-gray-100 text-xs text-gray-500">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between gap-4">
          <div>SkyElite Aviation SA · Lugano-Agno Airport · EASA Part-NCC certified</div>
          <div>Demo FlashWeb · <Link to="/" className="underline hover:text-gray-900">flash-web.ch</Link></div>
        </div>
      </footer>
    </div>
  );
}

export const Route = createFileRoute("/demo/aurora-jet")({
  head: () => ({
    meta: [
      { title: "SkyElite — Charter privati premium da Lugano (Demo FlashWeb)" },
      { name: "description", content: "Aviazione privata su misura: flotta certificata EASA, decollo in 4 ore da Lugano, Milano e Zurigo verso 190+ aeroporti. Preventivo istantaneo." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: SkyElite,
});
